import React, { useState } from 'react';
import { Image as ImageIcon, Video, Layers, Play } from 'lucide-react';
import DraftStateBadge from './DraftStateBadge';
import SlaTimer from './SlaTimer';
import DraftDecisionPanel from './DraftDecisionPanel';
import CreatorAvatar from '../creators/CreatorAvatar';

/**
 * DraftReviewCard — single-draft preview + Approve/Revise/Pass panel.
 * Pure presentational; parent owns the queue + refresh.
 */
export default function DraftReviewCard({ draft, onResolved }) {
  const [resolved, setResolved] = useState(false);
  const handleResolve = (info) => {
    setResolved(true);
    onResolved && onResolved({ id: draft.id, ...info });
  };

  if (resolved) {
    return (
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-300" data-testid="draft-resolved-banner">
        Decision sent to {draft.creator_handle || 'creator'}. Refreshing queue…
      </div>
    );
  }

  const TypeIcon = draft.media_type === 'video' ? Video
    : draft.media_type === 'carousel' ? Layers : ImageIcon;

  return (
    <div className="rounded-2xl border border-[#1f1f1f] bg-[#121212] overflow-hidden" data-testid={`draft-card-${draft.id}`}>
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-0">
        {/* LEFT — media + meta */}
        <div className="bg-black/40 p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreatorAvatar handle={draft.creator_handle} size={36} />
              <div>
                <p className="text-sm font-bold">{draft.creator_handle || draft.creator_name}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-widest">
                  {draft.platform} · {draft.role || 'creator'}
                </p>
              </div>
            </div>
            <SlaTimer draftSavedAt={draft.draft_saved_at} />
          </div>

          {draft.media_url ? (
            draft.media_type === 'video' ? (
              <video
                data-testid="draft-media-video"
                src={draft.media_url}
                controls
                className="w-full max-h-[420px] rounded-lg object-contain bg-black"
              />
            ) : (
              <img
                data-testid="draft-media-image"
                src={draft.media_url}
                alt="Creator draft"
                className="w-full max-h-[420px] rounded-lg object-contain bg-black"
              />
            )
          ) : draft.thumbnail_url ? (
            /* Demo-mode placeholder: real thumbnail + play-button overlay
               so the approval queue feels like a working video review even
               when no media URL is wired. */
            <div
              className="relative rounded-lg overflow-hidden aspect-video bg-black/60 group"
              data-testid="draft-video-placeholder"
            >
              <img
                src={draft.thumbnail_url}
                alt="Draft preview"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-md transition-transform group-hover:scale-110"
                  style={{
                    background: 'rgba(255,255,255,0.18)',
                    border: '1.5px solid rgba(255,255,255,0.5)',
                    boxShadow: '0 0 24px rgba(0,212,255,0.25)',
                  }}
                >
                  <Play className="h-6 w-6 text-white fill-white" />
                </div>
              </div>
              <div className="absolute bottom-2 left-2 text-[10px] uppercase tracking-widest text-white/80 font-semibold">
                <Video className="h-3 w-3 inline-block mr-1 -mt-0.5" />
                {draft.platform} · {Math.floor(Math.random() * 35 + 18)}s
              </div>
            </div>
          ) : (
            <div className="rounded-lg bg-black/60 p-6 text-center text-xs text-gray-500" data-testid="draft-no-media">
              <TypeIcon className="h-6 w-6 mx-auto mb-2 opacity-60" />
              No media uploaded yet — review the caption and copy below.
            </div>
          )}

          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500">
            <DraftStateBadge status={draft.status} />
            {draft.revision_round > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-300 border border-orange-500/20" data-testid="draft-revision-pill">
                Round {draft.revision_round + 1}
              </span>
            )}
          </div>
        </div>

        {/* RIGHT — caption + creator note + decision */}
        <div className="p-5 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Caption / script</p>
            <p className="text-sm whitespace-pre-line text-gray-200" data-testid="draft-caption">
              {draft.approved_caption || <span className="text-gray-500 italic">No caption supplied.</span>}
            </p>
          </div>

          {draft.creator_note && (
            <div className="rounded-lg bg-cyan-500/5 border border-cyan-500/20 p-3" data-testid="draft-creator-note">
              <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold mb-1">Note from creator</p>
              <p className="text-xs text-gray-200 leading-relaxed">{draft.creator_note}</p>
            </div>
          )}

          {draft.payment_amount > 0 && (
            <div className="text-[11px] text-gray-500">
              Escrow held: <span className="text-emerald-300 font-bold">${draft.payment_amount.toLocaleString()}</span>
            </div>
          )}

          <DraftDecisionPanel draft={draft} onResolved={handleResolve} />
        </div>
      </div>
    </div>
  );
}
